import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Link,
  IconButton,
  Tooltip,
  TextField,
  Button,
} from '@mui/material';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import api from '../services/api';

interface SupportTicketListItem {
  _id: string;
  source: 'public' | 'agency';
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  screenshotUrl?: string;
  createdAt: string;
  updatedAt: string;
  agencyId?: string;
  agencySlug?: string;
  agencyName?: string;
}

interface SupportTicketDetail extends SupportTicketListItem {
  responses?: Array<{
    text: string;
    isFromUser: boolean;
    senderName?: string;
    createdAt: string;
  }>;
}

const ScreenshotPreview: React.FC<{ url: string }> = ({ url }) => {
  const [error, setError] = useState(false);
  return error ? (
    <Box
      sx={{
        width: 280,
        height: 160,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.200',
        borderRadius: 1,
        color: 'text.secondary',
        fontSize: '0.875rem',
      }}
    >
      Preview unavailable
    </Box>
  ) : (
    <Box
      component="img"
      src={url}
      alt="Support ticket screenshot"
      onError={() => setError(true)}
      sx={{
        display: 'block',
        maxWidth: '100%',
        maxHeight: 400,
        objectFit: 'contain',
        borderRadius: 1,
      }}
    />
  );
};

const AdminSupportTickets = () => {
  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [selected, setSelected] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      const res = await api.get<SupportTicketListItem[]>(`/admin/support-tickets?${params}`);
      setTickets(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, sourceFilter]);

  const fetchDetail = async (ticket: SupportTicketListItem, silent = false) => {
    if (!silent) setDetailLoading(true);
    try {
      if (ticket.source === 'public') {
        const res = await api.get<SupportTicketDetail>(`/admin/support-tickets/public/${ticket._id}`);
        setSelected(res.data);
      } else {
        if (!ticket.agencyId) {
          setError('Agency ID missing for agency ticket');
          return;
        }
        const res = await api.get<SupportTicketDetail>(
          `/admin/support-tickets/agency/${ticket.agencyId}/${ticket._id}`
        );
        setSelected(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch ticket details');
      setSelected(null);
    } finally {
      if (!silent) setDetailLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'resolved':
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleReply = async () => {
    if (!selected || !replyText.trim()) return;
    setSendingReply(true);
    setError(null);
    try {
      if (selected.source === 'public') {
        await api.post(`/admin/support-tickets/public/${selected._id}/responses`, { text: replyText.trim() });
      } else if (selected.agencyId) {
        await api.post(
          `/admin/support-tickets/agency/${selected.agencyId}/${selected._id}/responses`,
          { text: replyText.trim() }
        );
      }
      setReplyText('');
      await fetchDetail(selected, true);
      fetchTickets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selected || !['open', 'in_progress', 'resolved', 'closed'].includes(newStatus)) return;
    if (newStatus === selected.status) return;
    setUpdatingStatus(true);
    setError(null);
    try {
      if (selected.source === 'public') {
        await api.patch(`/admin/support-tickets/public/${selected._id}/status`, { status: newStatus });
      } else if (selected.agencyId) {
        await api.patch(
          `/admin/support-tickets/agency/${selected.agencyId}/${selected._id}/status`,
          { status: newStatus }
        );
      }
      await fetchDetail(selected, true);
      fetchTickets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const selectedIndex = selected
    ? tickets.findIndex(
        (t) =>
          t._id === selected._id &&
          t.source === selected.source &&
          (t.source === 'agency' ? t.agencyId === selected.agencyId : true)
      )
    : -1;
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex >= 0 && selectedIndex < tickets.length - 1;
  const goToPrev = () => {
    if (!hasPrev || selectedIndex <= 0) return;
    fetchDetail(tickets[selectedIndex - 1]);
  };
  const goToNext = () => {
    if (!hasNext || selectedIndex < 0 || selectedIndex >= tickets.length - 1) return;
    fetchDetail(tickets[selectedIndex + 1]);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Support Tickets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View support requests from the public support page and from authenticated users (mobile app)
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 240px)', minHeight: 400 }}>
        <Paper sx={{ width: 380, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="agency">Agency</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <List sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : tickets.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No support tickets found
                </Typography>
              </Box>
            ) : (
              tickets.map((t) => (
                <ListItem
                  key={t.source === 'agency' ? `${t.agencyId}-${t._id}` : t._id}
                  button
                  selected={selected?._id === t._id && selected?.source === t.source}
                  onClick={() => fetchDetail(t)}
                  sx={{ borderBottom: 1, borderColor: 'divider', flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Chip label={t.source} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                    <Chip label={t.status} size="small" color={getStatusColor(t.status) as any} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5 }} noWrap>
                    {t.subject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ width: '100%' }}>
                    {t.name}
                    {t.agencyName && ` (${t.agencyName})`}
                    {` · ${t.email}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatTime(t.createdAt)}
                  </Typography>
                </ListItem>
              ))
            )}
          </List>
        </Paper>

        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : selected ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Tooltip title="Previous ticket">
                    <span>
                      <IconButton
                        size="small"
                        onClick={goToPrev}
                        disabled={!hasPrev || detailLoading}
                        aria-label="Previous ticket"
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Next ticket">
                    <span>
                      <IconButton
                        size="small"
                        onClick={goToNext}
                        disabled={!hasNext || detailLoading}
                        aria-label="Next ticket"
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary">
                    {selectedIndex + 1} of {tickets.length}
                  </Typography>
                  <Chip label={selected.source} size="small" variant="outlined" />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={selected.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={updatingStatus}
                      sx={{ height: 28, fontSize: '0.8125rem' }}
                    >
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="in_progress">In progress</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                  {selected.agencyName && (
                    <Chip label={`Agency: ${selected.agencyName}`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selected.subject}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selected.name}
                  {selected.agencyName && ` (${selected.agencyName})`}
                  {` · ${selected.email}`}
                  {selected.agencySlug && ` · ${selected.agencySlug}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {formatTime(selected.createdAt)}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selected.message}
                </Typography>
                {selected.screenshotUrl && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Screenshot
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        display: 'inline-block',
                        maxWidth: '100%',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <ScreenshotPreview url={selected.screenshotUrl} />
                    </Paper>
                    <Box sx={{ mt: 1 }}>
                      <Link
                        href={selected.screenshotUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <OpenInNewIcon fontSize="small" />
                        Open in new tab
                      </Link>
                    </Box>
                  </Box>
                )}
              {selected.responses && selected.responses.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Responses
                  </Typography>
                  {selected.responses.map((r, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {r.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.senderName || (r.isFromUser ? 'User' : 'Support')} · {formatTime(r.createdAt)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Reply
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={sendingReply}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 2000 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleReply}
                    disabled={sendingReply || !replyText.trim()}
                    startIcon={sendingReply ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    sx={{ alignSelf: 'flex-end', minWidth: 100 }}
                  >
                    {sendingReply ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
              </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <ContactSupportIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body1">Select a ticket to view details</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminSupportTickets;
